import React, { useEffect, useState } from 'react';
import { Database, Trash2, Clock, RefreshCw, Loader2, Send, Copy, Check, AlertTriangle, Users, Calendar } from 'lucide-react';
import { campaignsApi, CampaignRecord } from '../services/creatorApi';
import { crmContactsApi, BucketAssignProgress } from '../services/crmApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function SendBucketManager() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [campaigns, setCampaigns] = useState<CampaignRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [clearingBucket, setClearingBucket] = useState<string | null>(null);
  const [clearProgress, setClearProgress] = useState<BucketAssignProgress | null>(null);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState<string | null>(null);
  const [clearingExpired, setClearingExpired] = useState(false);
  const fetchBuckets = async () => {
    setLoading(true);
    try {
      const all = await campaignsApi.list();
      // Filter to current team, with a Bucket_ID, status = Exported
      const filtered = all.filter(c => {
        if (c.Team !== currentWorkspace.name) return false;
        if (currentSubRegion && c.Region && c.Region !== currentSubRegion) return false;
        return !!c.Bucket_ID;
      });
      // Sort by Exported_At descending
      filtered.sort((a, b) => {
        const da = a.Exported_At ? new Date(a.Exported_At).getTime() : 0;
        const db = b.Exported_At ? new Date(b.Exported_At).getTime() : 0;
        return db - da;
      });
      setCampaigns(filtered);
    } catch (err) {
      console.error('Failed to load buckets:', err);
      toast.error('Failed to load send buckets');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchBuckets();
  }, [currentWorkspace.name, currentSubRegion]);
  const handleClearBucket = async (bucketId: string, campaignId: string) => {
    setClearingBucket(bucketId);
    setClearProgress(null);
    setShowClearConfirm(null);
    try {
      const result = await crmContactsApi.clearBucket(bucketId, progress => setClearProgress(progress));
      if (result.success) {
        // Update campaign status in Creator
        await campaignsApi.update(campaignId, {
          Status: 'Sent',
          Bucket_ID: ''
        });
        toast.success(`Cleared bucket from ${result.clearedCount} contacts`);
      } else {
        toast.error(`Failed to clear bucket: ${result.errors[0] || 'Unknown error'}`);
      }
      fetchBuckets();
    } catch (err) {
      console.error('Failed to clear bucket:', err);
      toast.error('Failed to clear bucket');
    } finally {
      setClearingBucket(null);
      setClearProgress(null);
    }
  };
  const handleClearExpired = async () => {
    setClearingExpired(true);
    setClearProgress(null);
    try {
      const result = await crmContactsApi.clearExpiredBuckets(progress => setClearProgress(progress));
      if (result.success) {
        toast.success(`Cleared ${result.clearedCount} expired bucket assignments`);
      } else {
        toast.error(`Failed: ${result.errors[0] || 'Unknown error'}`);
      }
      fetchBuckets();
    } catch (err) {
      console.error('Failed to clear expired:', err);
      toast.error('Failed to clear expired buckets');
    } finally {
      setClearingExpired(false);
      setClearProgress(null);
    }
  };
  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(id);
    setTimeout(() => setCopiedItem(null), 2000);
  };
  const isExpired = (expiresAt?: string) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) <= new Date();
  };
  const formatExpiry = (expiresAt?: string) => {
    if (!expiresAt) return 'No expiry';
    const date = new Date(expiresAt);
    const now = new Date();
    if (date <= now) return 'Expired';
    const hoursLeft = Math.round((date.getTime() - now.getTime()) / (1000 * 60 * 60));
    if (hoursLeft < 1) return 'Expiring soon';
    if (hoursLeft < 24) return `${hoursLeft}h remaining`;
    return `${Math.round(hoursLeft / 24)}d remaining`;
  };
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };
  const expiredCount = campaigns.filter(c => isExpired(c.Bucket_Expires_At)).length;
  return <div className="space-y-6" data-id="element-5929" data-mp="19d2i2r3u0d7r">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-5930" data-mp="19feyv0xushj8">
        <div className="min-w-0" data-id="element-5931" data-mp="1eau6vc96jj7q">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-5932" data-mp="1jb8hn226179g">Send Buckets</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-5933" data-mp="33tso2ahbmqd5">
            Manage active audience buckets pushed to Zoho CRM. Clear buckets
            after sending campaigns.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-5934" data-mp="2tvmqu66gm8em">
          <button onClick={fetchBuckets} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Refresh" data-id="element-5935" data-mp="2twecopa98rrp">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-5936" data-mp="3mjijmzswrg64" />
          </button>
          {expiredCount > 0 && <button onClick={handleClearExpired} disabled={clearingExpired || !!clearingBucket} className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 disabled:opacity-50" data-id="element-5937" data-mp="3cuh57e20h6u9">
              {clearingExpired ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-5938" data-mp="4dmawmza4urs" /> : <Clock className="h-4 w-4" data-id="element-5939" data-mp="1ylzomk7jo6tx" />}
              Clear {expiredCount} Expired
            </button>}
        </div>
      </div>

      {/* Progress Bar (shown during clear operations) */}
      {(clearingBucket || clearingExpired) && clearProgress && <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-2" data-id="element-5940" data-mp="1tvj8gscca73v">
          <div className="flex items-center justify-between text-sm" data-id="element-5941" data-mp="1fw1gk43qydqa">
            <span className="text-purple-700 font-medium" data-id="element-5942" data-mp="1fuqce36e33yn">
              {clearingExpired ? 'Clearing expired buckets...' : `Clearing bucket ${clearingBucket}...`}
            </span>
            <span className="text-purple-900 font-mono text-xs" data-id="element-5943" data-mp="1606raq50pkej">
              Batch {clearProgress.currentBatch} / {clearProgress.totalBatches}
            </span>
          </div>
          <div className="w-full bg-purple-200 rounded-full h-2" data-id="element-5944" data-mp="30co7xevcpma7">
            <div className="bg-purple-600 h-2 rounded-full transition-all duration-300" style={{
          width: `${Math.round(clearProgress.processedContacts / clearProgress.totalContacts * 100)}%`
        }} data-id="element-5945" data-mp="359wstn7vw4f5" />
          </div>
          <p className="text-xs text-purple-600" data-id="element-5946" data-mp="13dxqbegu30jr">
            {clearProgress.processedContacts} / {clearProgress.totalContacts}{' '}
            contacts processed
          </p>
        </div>}

      {/* Buckets Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-5947" data-mp="1ccsu6jwenyjw">
        {loading && campaigns.length === 0 ? <div className="p-12 text-center" data-id="element-5948" data-mp="17h3r46l6fziu">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-5949" data-mp="1mcjq8q0m6lm3" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-5950" data-mp="1hdgkt4gq2ddl">
              Loading send buckets...
            </p>
          </div> : campaigns.length === 0 ? <div className="p-12 text-center" data-id="element-5951" data-mp="3o3wt79j0ujfi">
            <Database className="mx-auto h-12 w-12 text-gray-400" data-id="element-5952" data-mp="3j53ejds00mdr" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-5953" data-mp="3j6wmqxi9fenv">
              No Active Buckets
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-5954" data-mp="32a7ml63shgw8">
              Export a campaign to create a send bucket in Zoho CRM.
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-5955" data-mp="373aivlzyb4g4">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-5956" data-mp="3c0slyvraepg7">
              <thead className="bg-gray-50" data-id="element-5957" data-mp="3c23pcugtlv6s">
                <tr data-id="element-5958" data-mp="2edk2156xpogb">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5959" data-mp="2ee3cy1y63cku">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5960" data-mp="2oagchgge8kni">
                    Bucket ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5961" data-mp="3khadnm155st">
                    Recipients
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5962" data-mp="7k99l9pj9zli">
                    Expiry
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5963" data-mp="1o29mrpn8kz8c">
                    Exported
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5964" data-mp="1ecq16c852r9s">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-5965" data-mp="14esgfyphnclx">
                {campaigns.map(campaign => {
              const expired = isExpired(campaign.Bucket_Expires_At);
              const isClearing = clearingBucket === campaign.Bucket_ID;
              return <tr key={campaign.ID} className={`${expired ? 'bg-red-50/50' : ''} hover:bg-gray-50`} data-id="element-5966" data-mp="2uqyav67t9640">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-5967" data-mp="2ppsjpctstqwl">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" data-id="element-5968" data-mp="1dqjrty6s2ktu">
                          {campaign.Campaign_Name}
                        </div>
                        <div className="text-xs text-gray-500 truncate max-w-[200px]" data-id="element-5969" data-mp="1mst1ymy4wc6p">
                          {campaign.Email_Subject}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-5970" data-mp="u3drbyadslbb">
                        <div className="flex items-center gap-2" data-id="element-5971" data-mp="u1j2w9zwib2h">
                          <code className="text-xs font-mono bg-purple-50 text-purple-700 px-2 py-1 rounded border border-purple-100" data-id="element-5972" data-mp="q3v5n0aqk67f">
                            {campaign.Bucket_ID}
                          </code>
                          <button onClick={() => handleCopy(campaign.Bucket_ID, campaign.ID)} className="text-gray-400 hover:text-gray-600 p-1" title="Copy Bucket ID" data-id="element-5973" data-mp="z0vq4vuopk5l">
                            {copiedItem === campaign.ID ? <Check className="h-3.5 w-3.5 text-green-500" data-id="element-5974" data-mp="2muhcxkv0dmco" /> : <Copy className="h-3.5 w-3.5" data-id="element-5975" data-mp="28z58umuyqjj3" />}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-5976" data-mp="31qf2ulec9ugg">
                        <div className="flex items-center gap-1.5 text-sm text-gray-700" data-id="element-5977" data-mp="35q7ifi25h0vb">
                          <Users className="h-3.5 w-3.5 text-gray-400" data-id="element-5978" data-mp="2rs97ynqy9ol3" />
                          {campaign.Recipient_Count ? campaign.Recipient_Count.toLocaleString() : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-5979" data-mp="1lbx5iah3hkjp">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${expired ? 'bg-red-100 text-red-800' : campaign.Bucket_Expires_At ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-700'}`} data-id="element-5980" data-mp="1l916sjxfaru7">
                          <Clock className="h-3 w-3" data-id="element-5981" data-mp="semk1drvtnoc" />
                          {formatExpiry(campaign.Bucket_Expires_At)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-5982" data-mp="ik30w6a2me6v">
                        {formatDate(campaign.Exported_At)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-5983" data-mp="9iwfbisked6r">
                        <div className="flex items-center justify-end gap-2" data-id="element-5984" data-mp="4js82xp7apj2">
                          <button onClick={() => handleCopy(`Bucket_ID equals ${campaign.Bucket_ID}`, `filter-${campaign.ID}`)} className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded text-xs font-medium" title="Copy Zoho Campaigns filter" data-id="element-5985" data-mp="23vuou06fx5t1">
                            {copiedItem === `filter-${campaign.ID}` ? 'Copied!' : 'Copy Filter'}
                          </button>
                          <button onClick={() => setShowClearConfirm(campaign.ID)} disabled={isClearing || !!clearingBucket} className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded disabled:opacity-50" title="Clear Bucket" data-id="element-5986" data-mp="1sfzr4q6ytkt2">
                            {isClearing ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-5987" data-mp="3cypgp89vlsss" /> : <Trash2 className="h-4 w-4" data-id="element-5988" data-mp="3bhac26sznr4y" />}
                          </button>
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </div>

      {/* How it works */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6" data-id="element-5989" data-mp="3ud9xszvdhyg3">
        <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-5990" data-mp="3pfi543mwm8au">
          How Send Buckets Work
        </h3>
        <ol className="space-y-2 text-sm text-gray-600" data-id="element-5991" data-mp="7za0yis4z5n4">
          <li className="flex gap-2" data-id="element-5992" data-mp="80tiecpj3mqh">
            <span className="font-medium text-gray-900" data-id="element-5993" data-mp="2z484za4rd3g">1.</span>
            <span data-id="element-5994" data-mp="3204wka8rvqb">
              Create a campaign and build an audience — this pushes a{' '}
              <code className="bg-white px-1 rounded text-xs border" data-id="element-5995" data-mp="gxcd9uhblkpp">
                Bucket_ID
              </code>{' '}
              to each contact in Zoho CRM
            </span>
          </li>
          <li className="flex gap-2" data-id="element-5996" data-mp="vk183j0mn9pr">
            <span className="font-medium text-gray-900" data-id="element-5997" data-mp="10lqm97j5c3i2">2.</span>
            <span data-id="element-5998" data-mp="10j4bkz0yl7vm">
              In Zoho Campaigns, create a segment with:{' '}
              <code className="bg-white px-1 rounded text-xs border" data-id="element-5999" data-mp="ml61w6xl1zmg">
                Bucket_ID equals [your-bucket-id]
              </code>
            </span>
          </li>
          <li className="flex gap-2" data-id="element-6000" data-mp="cul5mq5dt76p">
            <span className="font-medium text-gray-900" data-id="element-6001" data-mp="ctjkfsunj4b9">3.</span>
            <span data-id="element-6002" data-mp="hn4kdqft28d3">
              Send your campaign through Zoho Campaigns using that segment
            </span>
          </li>
          <li className="flex gap-2" data-id="element-6003" data-mp="3wsmtc0gbhek">
            <span className="font-medium text-gray-900" data-id="element-6004" data-mp="7w9u8azfuomd">4.</span>
            <span data-id="element-6005" data-mp="7ugpj18dyeh0">
              After sending, <strong data-id="element-6006" data-mp="3q1mlf87bwnn7">clear the bucket</strong> here to free
              contacts for future campaigns
            </span>
          </li>
        </ol>
      </div>

      {/* Clear Confirmation Modal */}
      {showClearConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6007" data-mp="20lu48k9cu2sa">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-6008" data-mp="1vukdthfzevfc">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowClearConfirm(null)} data-id="element-6009" data-mp="1vshdo4l47lww" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-6010" data-mp="1mup2ncmff0fb">
              <div className="flex items-center gap-4 mb-4" data-id="element-6011" data-mp="2jivae8ena8xt">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-6012" data-mp="2fibdn5xzh7c7">
                  <Trash2 className="h-5 w-5 text-red-600" data-id="element-6013" data-mp="2aip0sxm2ht3x" />
                </div>
                <div data-id="element-6014" data-mp="25k40ky6tf9fh">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-6015" data-mp="25kn77ir0mbe7">
                    Clear Bucket
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-6016" data-mp="z4uomrqdf5th">
                    This will remove the Bucket ID from all contacts in CRM.
                    Only do this after you've sent the campaign.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3" data-id="element-6017" data-mp="ozv0l9d1meou">
                <button onClick={() => setShowClearConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-6018" data-mp="p0e95zgszqq7">
                  Cancel
                </button>
                <button onClick={() => {
              const campaign = campaigns.find(c => c.ID === showClearConfirm);
              if (campaign) handleClearBucket(campaign.Bucket_ID, campaign.ID);
            }} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700" data-id="element-6019" data-mp="12qq5kakf8bn5">
                  Clear Bucket
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}